import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import VideoCard from "@/components/base/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import { formatDate, formatViews, relativeDate } from "@/utils/format";
import VideoPlayer from "./components/VideoPlayer";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((item) => item.id === id);
  const [toast, setToast] = useState("");

  const category = categories.find((cat) => cat.id === video?.categoryId);

  usePageMeta({
    title: video ? `${video.title} - 黑料视频` : "未找到视频 - 黑料视频",
    description: video
      ? video.excerpt
      : "抱歉，未找到对应的视频内容。",
    keywords: video
      ? `${video.tags.join(",")},黑料视频,娱乐八卦`
      : "黑料视频,娱乐八卦",
  });

  const categoryNameMap: Record<string, string> = categories.reduce(
    (acc, cat) => ({ ...acc, [cat.id]: cat.name }),
    {},
  );

  const recommended = useMemo(() => {
    if (!video) return [];
    const sameCategory = videos.filter(
      (item) => item.categoryId === video.categoryId && item.id !== video.id,
    );
    const others = videos.filter(
      (item) => item.categoryId !== video.categoryId && item.id !== video.id,
    );
    return [...sameCategory, ...others]
      .sort((a, b) => b.views - a.views)
      .slice(0, 6);
  }, [video]);

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${window.location.pathname}`;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setToast("链接已复制，快分享给朋友吧");
    } catch {
      setToast(`复制失败，请手动复制：${shareUrl}`);
    }
    window.setTimeout(() => setToast(""), 2600);
  };

  if (!video) {
    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-20 md:px-6">
        <div className="flex flex-col items-center justify-center rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-error-warning-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-xl font-black text-foreground-950">
            没有找到这条视频
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            它可能已被移除，返回首页看看最新的娱乐热闻吧。
          </p>
          <Link
            to="/"
            className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            category
              ? { label: category.name, to: `/category/${category.id}` }
              : { label: "视频分类", to: "/" },
            { label: "视频详情" },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-3">
          {/* Main */}
          <div className="lg:col-span-2">
            <VideoPlayer cover={video.cover} title={video.title} duration={video.duration} />

            <h1 className="mt-6 font-heading text-xl font-black leading-snug text-foreground-950 md:text-2xl">
              {video.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-xs text-foreground-500 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-accent-500"></i>
                {video.source}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {formatDate(video.publishDate)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                时长 {video.duration}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-play-circle-line"></i>
                {formatViews(video.views)} 次播放
              </span>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              {category ? (
                <Link
                  to={`/category/${category.id}`}
                  className="flex items-center gap-2 rounded-full bg-secondary-100 px-4 py-1.5 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200 cursor-pointer whitespace-nowrap"
                >
                  <i className={`${category.icon} text-accent-600`}></i>
                  {category.name}
                </Link>
              ) : null}
              {video.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="rounded-full border border-background-300 px-3 py-1 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                >
                  #{tag}
                </Link>
              ))}
              <button
                type="button"
                onClick={handleShare}
                className="ml-auto flex items-center gap-2 rounded-md border border-background-300 px-4 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-share-forward-line"></i>
                分享
              </button>
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-black text-foreground-950">
                <i className="ri-file-text-line text-primary-500"></i>
                内容简介
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                {video.excerpt}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                {video.description}
              </p>
              <p className="mt-4 rounded-md bg-accent-50 px-4 py-3 text-xs leading-relaxed text-accent-900">
                <i className="ri-alert-line mr-1"></i>
                温馨提示：本视频为前端演示内容，播放器仅展示交互样式，不接入真实视频流，也不会播放任何实际画面。
              </p>
            </div>

            {/* Mobile share hint */}
            <p className="mt-4 text-xs text-foreground-400">
              发布于 {relativeDate(video.publishDate)} · 内容整理自公开信息
            </p>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-magic-line"></i>
              </span>
              <h2 className="font-heading text-base font-black text-foreground-950">
                猜你想看
              </h2>
            </div>
            <div className="mt-4 flex flex-col gap-4">
              {recommended.map((item) => (
                <Link
                  key={item.id}
                  to={`/detail/${item.id}`}
                  className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <div className="h-16 w-28 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 缩略图`}
                      title={`${item.title} - 黑料视频`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600">
                      {item.title}
                    </p>
                    <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-play-circle-line"></i>
                        {formatViews(item.views)}
                      </span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {item.duration}
                      </span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4">
              <h3 className="text-sm font-bold text-foreground-950">相关资讯</h3>
              <ul className="mt-3 flex flex-col divide-y divide-background-200">
                {recommended.slice(0, 4).map((item, i) => (
                  <li key={`rel-${item.id}`}>
                    <Link
                      to={`/detail/${item.id}`}
                      className="flex items-start gap-2 py-2.5 text-xs leading-relaxed text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer"
                    >
                      <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded bg-secondary-200 text-[10px] font-bold text-secondary-900">
                        {i + 1}
                      </span>
                      <span className="line-clamp-2">{item.title}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* Related grid */}
        <section className="mt-12">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-heading text-lg font-black text-foreground-950 md:text-xl">
              相关推荐视频
            </h2>
            {category ? (
              <Link
                to={`/category/${category.id}`}
                className="flex items-center gap-1 text-sm font-medium text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer whitespace-nowrap"
              >
                更多{category.name}
                <i className="ri-arrow-right-line"></i>
              </Link>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {recommended.map((item) => (
              <VideoCard
                key={`grid-${item.id}`}
                video={item}
                categoryName={categoryNameMap[item.categoryId]}
              />
            ))}
          </div>
        </section>
      </div>

      {/* Toast */}
      {toast ? (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 text-sm text-background-50 animate-fade-in">
          <span className="flex items-center gap-2">
            <i className="ri-check-line text-accent-400"></i>
            {toast}
          </span>
        </div>
      ) : null}
    </div>
  );
}